import { Box, Button, Card, CardBody, Stack, Text, Tooltip, useBreakpointValue } from '@chakra-ui/react';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { MindbodyClass } from 'types/class';
import { classHasCapacity, getClassRemainingCapacity, isClassBookable, stripHtml } from 'utils/functions';

// Ajout d'une fonction helper pour vérifier si la waitlist est pleine
const isWaitlistFull = (mindbodyClass: MindbodyClass) => {
  return mindbodyClass.TotalBookedWaitlist >= mindbodyClass.WaitListSize;
};

export function ClassCard({ mindbodyClass }: { mindbodyClass: MindbodyClass }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useBreakpointValue({ base: true, md: false });

  const isBookable = useMemo(() => isClassBookable(mindbodyClass), [mindbodyClass]);
  const isEnrollment = mindbodyClass.ClassDescription.Program.ScheduleType === 'Enrollment';

  function onClick() {
    if (isBookable) {
      if (mondayMindbody.coach) {
        window.open(`/reservation/#/class/${mindbodyClass.Id}`, '_blank');
      } else {
        navigate(`/class/${mindbodyClass.Id}`);
      }
    }
    if (isEnrollment) {
      if (mondayMindbody.coach) {
        window.open(`/reservation/#/class/${mindbodyClass.Id}/enrollment`, '_blank');
      } else {
        navigate(`/class/${mindbodyClass.Id}/enrollment`);
      }
    }
  }

  return (
    <Card
      height={isMobile ? 'auto' : '160'}
      borderBottom="0"
      borderTop="0"
      borderRadius="0"
      boxShadow="none"
      borderLeft="0"
      textAlign="center"
      onClick={onClick}
      cursor={isBookable || isEnrollment ? 'pointer' : 'initial'}
      opacity={isBookable || isEnrollment ? 1 : 0.5}
      className={isBookable || isEnrollment ? 'classCard classCardBookable' : 'classCard classCardClosed'}
      w="full"
    >
      <CardBody w="full" borderBottom={isMobile ? '1px solid #D9D9D9' : ''}>
        <Stack
          h="full"
          justifyContent={'space-between'}
          flexDirection={isMobile ? 'row' : 'column'}
          gap={0}
          alignItems="center"
        >
          <Box className="forMobile" h="full" alignItems="center" gap={0} textAlign={isMobile ? 'left' : 'center'}>
            <Text className="classHour" fontSize={{ base: 'lg', md: 'md' }} fontWeight="medium">
              {format(new Date(mindbodyClass.StartDateTime), 'HH:mm')}
            </Text>
            <Tooltip
              className="classDescriptionTooltip"
              sx={{
                '--chakra-colors-tooltip-bg': 'gray.100', // Fond gris clair
                '--chakra-colors-tooltip-text': 'black', // Texte noir
              }}
              label={stripHtml(mindbodyClass.ClassDescription.Description)}
            >
              <Text
                className="className"
                fontSize="md"
                marginTop={0}
                color="primary.500"
                fontWeight="medium"
                textTransform="lowercase"
              >
                {mindbodyClass.ClassDescription.Name}
              </Text>
            </Tooltip>
            <Text
              className="classCoach"
              fontSize={{ base: 'lg', md: 'md' }}
              marginTop={0}
              fontWeight="medium"
              textTransform="uppercase"
            >
              {mindbodyClass.Staff.FirstName}
            </Text>

            {(isBookable || isEnrollment) && (
              <Text fontSize={{ base: 'lg', md: 'md' }} fontWeight="thin" display="none">
                {t('{{ count }} place restante', { count: getClassRemainingCapacity(mindbodyClass) })}
              </Text>
            )}
          </Box>

          {(isBookable || isEnrollment) && (
            <Button
              mt={isMobile ? '0' : 'auto'}
              size="sm"
              colorScheme="primary"
              borderRadius={mondayMindbody?.customization?.buttonBorderRadius}
              textTransform="uppercase"
              className="bookButton"
            >
              {classHasCapacity(mindbodyClass)
                ? t('Réserver')
                : isWaitlistFull(mindbodyClass)
                ? t('Complet')
                : t("Liste d'attente")}
            </Button>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
}
